import { describeKnowledgeApiError } from 'utils/knowledgeErrors';
import { Drawer, DrawerProps, Button, Form, message, ButtonProps } from 'antd';
import { memo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { Formik } from 'formik';
import KnowledgeContentItem from '@knowledge/knowledges/spu/createOrEdit/formItem/KnowledgeContent';
import { css, cx } from '@emotion/css';
import FormField from 'components/FormField';
import type { Segment } from './type';
import { ValueAsDirectAnswerDrawer } from '@knowledge/knowledges/singleClass/ValueAsDirectAnswerDrawer';
import {
  ConfigDefaultCheckbox,
  useSetCheck,
} from '@knowledge/knowledges/singleClass/ConfigDefault';

export interface EditKCDrawerSelfProps {
  value: Segment[] | undefined;
  onChange: (value: Segment[], checked: boolean) => Promise<unknown>;
}
interface EditKCDrawerProps extends DrawerProps, EditKCDrawerSelfProps {}
export const EditKCDrawer = memo((props: EditKCDrawerProps) => {
  const { t } = useTranslate();
  const { className, value, onChange, ...restProps } = props;
  const [checked, setChecked] = useState(false);
  return (
    <Formik
      enableReinitialize
      initialValues={{
        value,
      }}
      onSubmit={(values) => {
        return onChange(values.value!, checked)
          .then(() => {
            props.onClose?.(null!);
          })
          .catch((_) => {
            message.error(
              describeKnowledgeApiError(_, undefined, t('phrase.保存失败，请稍后再试')),
            );
          });
      }}
    >
      {({ handleSubmit, dirty, isSubmitting }) => {
        return (
          <Drawer
            className={cx(
              css`
                .ant-drawer-body {
                  padding: 16px;
                }
              `,
              className,
            )}
            width={640}
            footer={
              <div className="flex gap-[16px] flex items-center">
                <ConfirmButton
                  checked={checked}
                  loading={isSubmitting}
                  type="primary"
                  disabled={!dirty}
                  onClick={() => handleSubmit()}
                >
                  {t('phrase.保存')}
                </ConfirmButton>
                <Button onClick={() => props.onClose?.(null!)}>{t('phrase.取消')}</Button>
                <ConfigDefaultCheckbox
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                />
              </div>
            }
            onClose={() => {
              props.onClose?.(null!);
            }}
            title={
              value
                ? t('knowledge.编辑x', { x: t('knowledge.知识值') })
                : t('knowledge.新增x', { x: t('knowledge.知识值') })
            }
            {...restProps}
          >
            <Form layout="vertical">
              <FormField name="value" required label={t('knowledge.知识值')}>
                {({ field }) => {
                  return <KnowledgeContentItem {...field} />;
                }}
              </FormField>
            </Form>
          </Drawer>
        );
      }}
    </Formik>
  );
});

const ConfirmButton = (props: ButtonProps & { checked: boolean }) => {
  const { onClick, checked, ...restProps } = props;
  const checkFn = useSetCheck(onClick!, () => checked);
  return <Button onClick={checkFn} {...restProps} />;
};

EditKCDrawer.displayName = 'SingleClassNewEditKCDrawer';

export interface EditDangerousKCDrawerSelfProps {
  regions: string[];
  defaultValues?: Record<string, Segment[]>;
  onConfirm: (values: Record<string, Segment[]>, checked: boolean) => Promise<unknown>;
}
interface EditDangerousKCDrawerProps extends DrawerProps, EditDangerousKCDrawerSelfProps {}
export const EditDangerousKCDrawer = memo((props: EditDangerousKCDrawerProps) => {
  const { className, regions, defaultValues, onConfirm, ...restProps } = props;
  const { t } = useTranslate();
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const handleConfirm = (res: any) => {
    setLoading(true);
    onConfirm(res as unknown as any, checked)
      .then(() => {
        restProps?.onClose?.(null!);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const checkFn = useSetCheck(handleConfirm, () => checked);
  return (
    <ValueAsDirectAnswerDrawer
      regions={regions as any}
      defaultValues={defaultValues as any}
      title={
        defaultValues
          ? t('knowledge.编辑x', { x: t('knowledge.知识值') })
          : t('knowledge.新增x', { x: t('knowledge.知识值') })
      }
      setDefaultNode={
        <ConfigDefaultCheckbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />
      }
      onConfirm={checkFn}
      saveLoading={loading}
      {...restProps}
    />
  );
});

EditDangerousKCDrawer.displayName = 'SingleClassNewEditDangerousKCDrawer';
